const GUIDE_MEDIA_GLOBAL_KEY = "__VALOVATE_GUIDE_MEDIA_MANIFEST__";
const GUIDE_RUNTIME_MEDIA_MANIFEST_PATH =
  "guide-media/runtime/published-guide-media-manifest.json";
const GUIDE_DEFAULT_STORAGE_BUCKET =
  (
    (typeof process !== "undefined" && process?.env?.REACT_APP_FIREBASE_STORAGE_BUCKET) ||
    "theultimatesaladclanguide.firebasestorage.app"
  ).trim();

let runtimeGuideMediaManifest = null;
let runtimeGuideMediaManifestLoaded = false;
let runtimeGuideMediaManifestLoadPromise = null;
let guideMediaManifestVersion = 0;
const guideMediaManifestListeners = new Set();

function getGlobalScope() {
  return typeof window !== "undefined" ? window : null;
}

function getStaticGuideMediaManifest() {
  const globalScope = getGlobalScope();
  const manifest = globalScope?.[GUIDE_MEDIA_GLOBAL_KEY];

  if (!manifest || typeof manifest !== "object") {
    return null;
  }

  return manifest;
}

function buildGuideRuntimeManifestUrl() {
  const bucketName = GUIDE_DEFAULT_STORAGE_BUCKET || "theultimatesaladclanguide.firebasestorage.app";
  return `https://firebasestorage.googleapis.com/v0/b/${bucketName}/o/${encodeURIComponent(
    GUIDE_RUNTIME_MEDIA_MANIFEST_PATH,
  )}?alt=media`;
}

function normalizeGuideRuntimeAssetEntry(assetPath, value) {
  if (!assetPath || !value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const remoteUrl =
    typeof value.remoteUrl === "string" && value.remoteUrl.trim() ? value.remoteUrl.trim() : "";
  if (!remoteUrl) {
    return null;
  }

  return {
    ...value,
    assetPath,
    remoteUrl,
    storagePath:
      typeof value.storagePath === "string" && value.storagePath.trim() ? value.storagePath.trim() : null,
    cacheControl:
      typeof value.cacheControl === "string" && value.cacheControl.trim() ? value.cacheControl.trim() : null,
    mimeType:
      typeof value.mimeType === "string" && value.mimeType.trim() ? value.mimeType.trim() : null,
    slotId: typeof value.slotId === "string" && value.slotId.trim() ? value.slotId.trim() : null,
    publishedAt:
      typeof value.publishedAt === "string" && value.publishedAt.trim() ? value.publishedAt.trim() : null,
    publishedByUid:
      typeof value.publishedByUid === "string" && value.publishedByUid.trim() ?
        value.publishedByUid.trim() :
        null,
  };
}

function normalizeGuideRuntimeManifest(value) {
  const rawManifest =
    value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const rawAssets =
    rawManifest.assets && typeof rawManifest.assets === "object" && !Array.isArray(rawManifest.assets) ?
      rawManifest.assets :
      {};
  const normalizedAssets = {};

  Object.entries(rawAssets).forEach(([assetPath, assetEntry]) => {
    const normalizedEntry = normalizeGuideRuntimeAssetEntry(assetPath, assetEntry);
    if (normalizedEntry) {
      normalizedAssets[assetPath] = normalizedEntry;
    }
  });

  return {
    schemaVersion:
      Number.isFinite(Number(rawManifest.schemaVersion)) && Number(rawManifest.schemaVersion) > 0 ?
        Number(rawManifest.schemaVersion) :
        1,
    updatedAt:
      typeof rawManifest.updatedAt === "string" && rawManifest.updatedAt.trim() ?
        rawManifest.updatedAt.trim() :
        null,
    updatedByUid:
      typeof rawManifest.updatedByUid === "string" && rawManifest.updatedByUid.trim() ?
        rawManifest.updatedByUid.trim() :
        null,
    assets: normalizedAssets,
  };
}

function notifyGuideMediaManifestListeners() {
  guideMediaManifestVersion += 1;
  guideMediaManifestListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error("Guide media manifest listener failed", error);
    }
  });
}

export function subscribeGuideMediaManifest(listener) {
  if (typeof listener !== "function") {
    return () => {};
  }

  guideMediaManifestListeners.add(listener);
  return () => {
    guideMediaManifestListeners.delete(listener);
  };
}

export function getGuideMediaManifestVersion() {
  return guideMediaManifestVersion;
}

export function getGuideMediaManifest() {
  const staticManifest = getStaticGuideMediaManifest();
  const runtimeManifest = runtimeGuideMediaManifest;

  if (!runtimeManifest) {
    return staticManifest;
  }

  const staticAssets =
    staticManifest?.assets && typeof staticManifest.assets === "object" ? staticManifest.assets : {};

  return {
    ...(staticManifest || {}),
    ...runtimeManifest,
    assets: {
      ...staticAssets,
      ...runtimeManifest.assets,
    },
  };
}

export async function ensureGuideMediaManifestLoaded(options = {}) {
  if (typeof window === "undefined" || typeof fetch !== "function") {
    return getGuideMediaManifest();
  }

  const forceRefresh = options?.force === true;
  if (runtimeGuideMediaManifestLoaded && !forceRefresh) {
    return getGuideMediaManifest();
  }

  if (runtimeGuideMediaManifestLoadPromise && !forceRefresh) {
    await runtimeGuideMediaManifestLoadPromise;
    return getGuideMediaManifest();
  }

  runtimeGuideMediaManifestLoadPromise = fetch(buildGuideRuntimeManifestUrl(), {
    cache: "no-store",
  })
    .then(async (response) => {
      if (response.status === 404) {
        runtimeGuideMediaManifest = {
          schemaVersion: 1,
          updatedAt: null,
          updatedByUid: null,
          assets: {},
        };
        runtimeGuideMediaManifestLoaded = true;
        notifyGuideMediaManifestListeners();
        return runtimeGuideMediaManifest;
      }

      if (!response.ok) {
        throw new Error(`Guide media runtime manifest request failed with ${response.status}.`);
      }

      const parsed = await response.json();
      runtimeGuideMediaManifest = normalizeGuideRuntimeManifest(parsed);
      runtimeGuideMediaManifestLoaded = true;
      notifyGuideMediaManifestListeners();
      return runtimeGuideMediaManifest;
    })
    .catch((error) => {
      console.warn("Failed to load runtime guide media manifest", error);
      if (!runtimeGuideMediaManifest) {
        runtimeGuideMediaManifest = {
          schemaVersion: 1,
          updatedAt: null,
          updatedByUid: null,
          assets: {},
        };
      }
      runtimeGuideMediaManifestLoaded = true;
      notifyGuideMediaManifestListeners();
      return runtimeGuideMediaManifest;
    })
    .finally(() => {
      runtimeGuideMediaManifestLoadPromise = null;
    });

  await runtimeGuideMediaManifestLoadPromise;
  return getGuideMediaManifest();
}

export function getGuideMediaAsset(assetPath) {
  if (typeof assetPath !== "string" || !assetPath) {
    return null;
  }

  const manifest = getGuideMediaManifest();
  const assetEntry = manifest?.assets?.[assetPath];
  return assetEntry && typeof assetEntry === "object" ? assetEntry : null;
}

export { GUIDE_MEDIA_GLOBAL_KEY, GUIDE_RUNTIME_MEDIA_MANIFEST_PATH };
